import { useMutation, useQuery } from "@tanstack/react-query";
import {
  PublicSharedUserApi,
  PublicSharedUserApi$VerifyEmailAndSetPasswordInput,
} from "@/custolink-transpiled_by_server/bc.identity_and_access.api_methods";
import Loading from "@/components/shared/Loading";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import Alert from "@/components/ui/Alert/Alert";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import useFormServerBacked from "@/custolink-hooks/form/useFormServerBacked";
import useFormErrorPicker from "@/custolink-hooks/form/useFormErrorPicker";
import { FormItem } from "@/components/ui";

export default function VerifyEmailSharedForm(p: {
  token: string;
  signInUrl: string;
  /**
   * This component is shared between the admin area (admin/auth/VerifyEmailForUser.tsx) and
   * the customer-facing one (end_user/VerifyEmailForEndUser.tsx) - it cannot tell which side
   * it is on by itself, so the caller says. Admin-area callers leave this unset; the
   * end_user caller passes "asset" so its validation toast follows the asset-frontend switch.
   */
  toastDesignSwitch?: "admin" | "asset";
}) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mode, setMode] = useState<"initial" | "confirm" | "set_password">(
    "initial",
  );

  const u = useQuery({
    queryFn: () => PublicSharedUserApi.getVerifyEmailTokenInfo(token ?? ""),
    queryKey: ["initialConfirmInvitationToken", token],
  });

  useEffect(() => {
    if (u.data != null) {
      if (u.data.ok) {
        if (u.data.mustSetPassword) {
          setMode("set_password");
        } else {
          setMode("confirm");
        }
      } else {
        setErrorMessage(u.data.errorMessageIfNotOk);
      }
    }
  }, [u.data]);

  if (u.isPending) {
    return <Loading loading={true} />;
  }

  if (errorMessage != null) {
    return (
      <>
        <Alert type={"warning"} className={"mt-4"}>
          {errorMessage}
        </Alert>
      <Button block variant="solid" type="button" onClick={()=>{
        navigate(p.signInUrl);
      }}>
        Continue
      </Button>
    </>
    );
  }

  return (
    <>
      {mode == "confirm" && <ConfirmNow token={p.token} signInUrl={p.signInUrl} />}
      {mode == "set_password" && (
        <SetPasswordForm
          token={p.token}
          signInUrl={p.signInUrl}
          toastDesignSwitch={p.toastDesignSwitch}
        />
      )}
    </>
  );
}

function ConfirmNow(p: { token: string, signInUrl: string }) {
  const [done, setDone] = React.useState(false);
  const navigate = useNavigate();
  const m = useMutation({
    mutationFn: (token: string) =>
      PublicSharedUserApi.verifyEmailAndSetPassword({
        token: token,
        newPassword: null,
      }),
    mutationKey: ["verifyEmailAndSetPassword"],
  });

  async function verify() {
    try {
      await m.mutateAsync(p.token);
      setDone(true);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    verify();
  }, []);

  if (done) {
    return (
      <div className={"space-y-4"}>
        <h3 className="mt-4">Thank you</h3>
        <p className="font-semibold heading-text">
          Your email address has verified. From now on, please use this email to
          sign in. All future communications will be sent to this address.
        </p>
        <Button block variant="solid" type="button" onClick={()=>{
          navigate(p.signInUrl);
        }}>
          Continue
        </Button>
      </div>
    );
  }

  return <Loading loading={true} />;
}

function SetPasswordForm(p: { token: string, signInUrl: string, toastDesignSwitch?: "admin" | "asset" }) {
  const [repeat, setRepeat] = React.useState("");
  const navigate = useNavigate();
  const [repeatError, setRepeatError] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const form =
    useFormServerBacked<PublicSharedUserApi$VerifyEmailAndSetPasswordInput>(
      {
        token: p.token,
        newPassword: "",
      },
      (d) => {
        if (d.newPassword != repeat) {
          setRepeatError(true);
          return Promise.reject();
        } else {
          setRepeatError(false);
        }
        return PublicSharedUserApi.verifyEmailAndSetPassword(d);
      },
      () => {
        form.reset({ newPassword: "" });
        setRepeat("");
        setDone(true);
      },
      undefined,
      p.toastDesignSwitch,
    );

  const formErrors = useFormErrorPicker(form.errors, "newPassword");

  if (done) {
    return (
      <div className={"space-y-4"}>
        <h3 className="mt-4">Thank you</h3>
        <p className="font-semibold heading-text">
          Your email address has verified. From now on, please use this email to
          sign in. All future communications will be sent to this address.
        </p>
        <Button block variant="solid" type="button" onClick={()=>{
          navigate(p.signInUrl);
        }}>
          Continue
        </Button>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={form.handleSubmit()}>
        <div className={"space-y-4"}>
          <p className="font-semibold heading-text">
            To continue, please choose a password for your account, and click
            Save below.
          </p>

          <FormItem
            label="New Password"
            invalid={formErrors.newPassword != undefined}
            errorMessage={formErrors.newPassword}
          >
            <Input
              {...form.register("newPassword")}
              type="password"
              autoComplete="off"
              placeholder="New Password"
            />
          </FormItem>

          <FormItem
            label="Repeat New Password"
            invalid={repeatError}
            errorMessage={
              repeatError
                ? "The new password does not match this one."
                : undefined
            }
          >
            <Input
              value={repeat}
              type="password"
              autoComplete="off"
              placeholder="Repeat New Password"
              onChange={(ev) => setRepeat(ev.target.value)}
            />
          </FormItem>

          <div className="flex justify-end">
            <Button
              variant="solid"
              type="submit"
              disabled={!form.isDirty}
              loading={form.isSubmitting}
            >
              Save
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
